import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { store } from 'src/infra/stores/store';
import { selectSelectedBilledEntities } from 'src/infra/stores/reducer/billedEntitiesSlice';
import { userStore } from 'src/use-cases/user/userDetails/userStore';
import { FatalError } from 'src/infra/services/FatalError';
import { RetriableError } from 'src/infra/services/RetriableError';
import { apiConfig } from 'src/config';

import {
  selectSSEState,
  connectionChanged,
  retryAttemptStarted,
  connectionFailed,
  retryStateReset,
  manualRetryRequested,
} from 'src/infra/stores/reducer/sseSlice';
import type { AppDispatch } from 'src/infra/stores/store';
import { routeSSEEvent } from './sseEventRouter';

/**
 * Get billed entity IDs for the x-billed-entity-ids header
 */
const getBilledEntityIds = (): string | null => {
  const state = store.getState();
  const selectedEntities = selectSelectedBilledEntities(state);

  const getAllEntityCodes = (entities: any[]): string[] => {
    let codes: string[] = [];
    entities.forEach((entity) => {
      if (entity.selected) {
        codes.push(entity.id);
      }
      if (entity.subEntities && entity.subEntities.length > 0) {
        codes = [...codes, ...getAllEntityCodes(entity.subEntities)];
      }
    });
    return codes;
  };

  const entityCodes = getAllEntityCodes(selectedEntities);
  if (entityCodes.length > 0) {
    return entityCodes.join(',');
  }

  const defaultBilledEntityId = userStore.getDefaultBilledEntityId();
  if (defaultBilledEntityId) {
    return defaultBilledEntityId;
  }

  return null;
};

/**
 * Component to initialize and manage the single SSE connection for the entire app
 * This is the ONLY place where SSE connections are created
 */
export const SSEInitializer = () => {
  const dispatch = useDispatch<AppDispatch>();
  const sseState = useSelector(selectSSEState);
  const connectionAttemptRef = useRef(false);

  // Get SSE endpoint from environment
  const sseUrl = process.env.NODE_ENV === 'production' ? apiConfig.tfinapiSseEndpoint : apiConfig.tfinapiSseEndpointDev;

  /**
   * Connect to SSE endpoint - THE ONLY PLACE this happens
   */
  const connect = useCallback(
    async (token?: string) => {
      console.log('🔗 SSE Initializer: Connect called', { url: sseUrl });

      // Prevent multiple simultaneous connection attempts
      if (connectionAttemptRef.current) {
        console.log('⚠️ SSE: Connection attempt already in progress, skipping');
        return;
      }

      const currentState = store.getState().sse;

      if (currentState.isConnected) {
        console.log('⚠️ SSE: Already connected, skipping connection attempt');
        return;
      }

      if (currentState.retryState.isRetrying) {
        console.log('⚠️ SSE: Connection already in progress, skipping');
        return;
      }

      connectionAttemptRef.current = true;

      try {
        const headers: Record<string, string> = {
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache',
        };

        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const billedEntityIds = getBilledEntityIds();
        if (billedEntityIds) {
          headers['x-billed-entity-ids'] = billedEntityIds;
        }

        await fetchEventSource(sseUrl, {
          method: 'GET',
          headers,
          openWhenHidden: true,

          onopen: async (response) => {
            console.log('🔗 SSE: Connection opened', { status: response.status, ok: response.ok });

            if (response.ok && response.headers.get('content-type')?.includes('text/event-stream')) {
              dispatch(connectionChanged(true));
              connectionAttemptRef.current = false; // Reset flag on success
              return;
            }

            if (response.status >= 400 && response.status < 500 && response.status !== 429) {
              throw new FatalError(`Client error: ${response.status} ${response.statusText}`);
            }

            throw new RetriableError(`Server error: ${response.status} ${response.statusText}`);
          },

          onmessage: (event) => {
            // Route the event to appropriate handler
            routeSSEEvent(event, dispatch);
          },

          onclose: () => {
            console.log('🔌 SSE: Connection closed');
            dispatch(connectionChanged(false));
            throw new RetriableError('Connection closed unexpectedly');
          },

          onerror: (err) => {
            console.log('❌ SSE: Connection error:', err);
            dispatch(connectionChanged(false));

            if (err instanceof FatalError) {
              dispatch(
                connectionFailed({
                  reason: err.message,
                  attempts: store.getState().sse.retryState.attempts,
                })
              );
              throw err;
            }

            const currentRetryState = store.getState().sse.retryState;
            const nextAttempt = currentRetryState.attempts + 1;

            if (nextAttempt >= currentRetryState.maxAttempts) {
              dispatch(
                connectionFailed({
                  reason: 'Maximum retry attempts reached',
                  attempts: nextAttempt,
                })
              );
              throw new FatalError('Maximum retry attempts reached');
            }

            const delay = Math.min(1000 * 2 ** (nextAttempt - 1), 30000);
            const nextRetryAt = new Date(Date.now() + delay).toISOString();
            dispatch(retryAttemptStarted({ attempt: nextAttempt, nextRetryAt }));

            return delay;
          },
        });
      } catch (error) {
        console.error('❌ SSE: Connection setup failed:', error);
        dispatch(connectionChanged(false));
        connectionAttemptRef.current = false; // Reset flag on error
        throw error;
      }
    },
    [dispatch, sseUrl]
  );

  /**
   * Initialize SSE connection on mount
   */
  useEffect(() => {
    const checkAuthAndConnect = async () => {
      try {
        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession();
        const isAuthenticated = !!session.tokens?.accessToken;

        const currentState = store.getState().sse;
        if (isAuthenticated && !currentState.isConnected && !currentState.retryState.isRetrying) {
          const token = session.tokens?.accessToken?.toString();
          await connect(token);
        }
      } catch (error) {
        console.log('SSE: Auth check failed, skipping connection:', error);
      }
    };

    checkAuthAndConnect();
  }, [connect]);

  useEffect(() => {
    console.log('🔗 SSE Initializer: Connection status changed', {
      isConnected: sseState.isConnected,
      lastActivity: sseState.lastActivity,
    });
  }, [sseState.isConnected, sseState.lastActivity]);

  /**
   * Handle manual retry requests
   */
  useEffect(() => {
    const handleManualRetry = async () => {
      if (sseState.retryState.isRetrying && sseState.retryState.attempts === 0) {
        // This indicates a manual retry was requested
        try {
          const { fetchAuthSession } = await import('aws-amplify/auth');
          const session = await fetchAuthSession();
          const token = session.tokens?.accessToken?.toString();

          if (token) {
            await connect(token);
          } else {
            throw new Error('No authentication token available');
          }
        } catch (error) {
          console.error('SSE: Manual retry failed:', error);
          dispatch(
            connectionFailed({
              reason: error instanceof Error ? error.message : String(error),
              attempts: 1,
            })
          );
        }
      }
    };

    handleManualRetry();
  }, [sseState.retryState.isRetrying, sseState.retryState.attempts, connect, dispatch]);

  // This component doesn't render anything - it just manages the SSE connection
  return null;
};
